import { createLoader } from '@meteor/data-hooks';
import PQueue from 'p-queue';
import { withAuth } from '../../user';
import { agentClient } from '../client';

export let stepsLoader = createLoader({
  name: 'steps',
  hash: i => i.agentId + i.instanceId + i.flowId,
  fetch: (i: { instanceId: string; agentId: string; flowId: string }) =>
    withAuth(() => agentClient.step.list(i)),
  mutators: {}
});

export let useSteps = (
  instanceId: string | null | undefined,
  agentId: string | null | undefined,
  flowId: string | null | undefined
) => {
  return stepsLoader.use(
    instanceId && agentId && flowId ? { instanceId, agentId, flowId } : null
  );
};

export interface CurrentFlowParams {
  instanceId: string;
  agentId: string;
  flowId: string;
}

let currentFlowParams: CurrentFlowParams | null = null;
export let setCurrentFlowParams = (i: CurrentFlowParams) => {
  currentFlowParams = i;
};

let getCurrentFlowParams = () => {
  if (!currentFlowParams) throw new Error('Current flow params not set');
  return currentFlowParams;
};

let wrapWithCurrentFlowParams =
  <I, O>(fn: (i: I & CurrentFlowParams) => Promise<O>) =>
  (i: Omit<I, 'instanceId' | 'agentId' | 'flowId'>) =>
    withAuth(() => fn({ ...i, ...getCurrentFlowParams() } as any));

let addInputOp = wrapWithCurrentFlowParams(agentClient.step.addInput);
let connectOp = wrapWithCurrentFlowParams(agentClient.step.connect);
let createOp = wrapWithCurrentFlowParams(agentClient.step.create);
let deleteOp = wrapWithCurrentFlowParams(agentClient.step.delete);
let disconnectOp = wrapWithCurrentFlowParams(agentClient.step.disconnect);
let removeInputsOp = wrapWithCurrentFlowParams(agentClient.step.removeInputs);
let setPositionOp = wrapWithCurrentFlowParams(agentClient.step.setPosition);
let updateOp = wrapWithCurrentFlowParams(agentClient.step.update);
let updateKeyOp = wrapWithCurrentFlowParams(agentClient.step.updateKey);
let setProviderConnectionOp = wrapWithCurrentFlowParams(
  agentClient.step.setProviderConnection
);

let queue = new PQueue({ concurrency: 1 });

export let flowOps = () => {
  return {
    addInput: async (...args: Parameters<typeof addInputOp>) =>
      (await queue.add(() => addInputOp(...args)))!,
    connect: async (...args: Parameters<typeof connectOp>) =>
      (await queue.add(() => connectOp(...args)))!,
    create: async (...args: Parameters<typeof createOp>) =>
      (await queue.add(() => createOp(...args)))!,
    delete: async (...args: Parameters<typeof deleteOp>) =>
      (await queue.add(() => deleteOp(...args)))!,
    disconnect: async (...args: Parameters<typeof disconnectOp>) =>
      (await queue.add(() => disconnectOp(...args)))!,
    removeInputs: async (...args: Parameters<typeof removeInputsOp>) =>
      (await queue.add(() => removeInputsOp(...args)))!,
    setPosition: async (...args: Parameters<typeof setPositionOp>) =>
      (await queue.add(() => setPositionOp(...args)))!,
    update: async (...args: Parameters<typeof updateOp>) =>
      (await queue.add(() => updateOp(...args)))!,
    updateKey: async (...args: Parameters<typeof updateKeyOp>) =>
      (await queue.add(() => updateKeyOp(...args)))!,
    setProviderConnection: async (...args: Parameters<typeof setProviderConnectionOp>) =>
      (await queue.add(() => setProviderConnectionOp(...args)))!
  };
};
